<template>
  <div>
    <Head :title="is_new ? 'Nowa aktualność' : (displayTitle || 'Edycja aktualności')" />

    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-500 hover:text-indigo-800" :href="routes.index()">Aktualności</Link>
      <span class="font-medium text-indigo-400">/</span>
      {{ is_new ? 'Nowa' : displayTitle }}
    </h1>

    <div class="w-full">
      <article-form
        :form="form"
        :editing-locale="editingLocale"
        :site-context-options="siteContextOptionsList"
        :existing-cover-url="existingCoverUrl"
        @submit="submit"
      >
        <footer
          class="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-100 bg-white/85 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] backdrop-blur-md lg:left-64"
        >
          <div class="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3 sm:px-6 lg:px-8">
            <div class="flex shrink-0 items-center gap-2.5">
              <span class="text-sm font-medium text-gray-700">Status publikacji</span>
              <toggle-switch
                v-model="isPublished"
                :errors="statusErrors"
              />
            </div>
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <div class="flex min-w-0 shrink-0 flex-wrap items-center gap-2.5">
              <label class="text-sm font-medium text-gray-700" for="article-published-at">
                Data publikacji
              </label>
              <input
                id="article-published-at"
                v-model="form.article.published_at"
                type="datetime-local"
                class="form-input h-9 w-auto min-w-0 text-sm"
                title="Wpis pojawi się na stronie dopiero gdy ta data minie (czas serwera UTC). Przy pierwszej publikacji z szkicu data w przeszłości jest ustawiana automatycznie."
              >
            </div>
            <div class="hidden h-8 w-px shrink-0 bg-gray-200 sm:block" />
            <locale-editor-switch v-model="editingLocale" />
            <div class="ml-auto flex shrink-0 items-center gap-2">
              <button
                v-if="!is_new"
                class="inline-flex h-9 min-w-24 items-center justify-center gap-1.5 rounded-lg bg-red-50 px-4 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
                type="button"
                title="Usuń"
                @click="destroy"
              >
                <icon name="trash" class="h-3.5 w-3.5 stroke-current" />
                Usuń
              </button>
              <loading-button
                :loading="form.processing"
                class="inline-flex h-9 min-w-24 items-center justify-center rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                type="submit"
              >
                {{ is_new ? 'Utwórz' : 'Zapisz' }}
              </loading-button>
            </div>
          </div>
        </footer>
      </article-form>

      <version-history
        v-if="!is_new"
        class="mt-8 pb-28"
        :versions="versions"
      />
    </div>
  </div>
</template>

<script>
import { createLayoutHelper } from '@/utils/layoutHelper';

export default {
  layout: createLayoutHelper,
};
</script>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { Head, Link, router, useForm, usePage } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import LocaleEditorSwitch from '@/Shared/LocaleEditorSwitch.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import VersionHistory from '@/Shared/VersionHistory.vue';
import ArticleForm from './Form.vue';
import { createArticleRouteHelper } from '@/utils/createArticleRouteHelper';

const { proxy } = getCurrentInstance();
const $routes = proxy.$routes;

const props = defineProps({
  article: { type: Object, required: true },
  // eslint-disable-next-line vue/prop-name-casing
  site_context_options: { type: Array, default: () => [] },
  // eslint-disable-next-line vue/prop-name-casing
  is_new: { type: Boolean, default: false },
  versions: { type: Array, default: () => [] },
});

const initialPublishedAt = props.article.published_at
  ? new Date(props.article.published_at).toISOString().slice(0, 16)
  : '';

const emptyLocale = () => ({ title: '', excerpt: '', body_html: '' });

const form = useForm({
  article: {
    slug:         props.article.slug || '',
    site_context: props.article.site_context || 'code_medical_center',
    status:       props.article.status || 'draft',
    published_at: initialPublishedAt,
    cover_image:  null,
    data_pl:      { ...emptyLocale(), ...(props.article.data_pl || {}) },
    data_en:      { ...emptyLocale(), ...(props.article.data_en || {}) },
  },
});

const editingLocale = ref('pl');
const existingCoverUrl = ref(props.article.cover_image_url || null);

const routes = computed(() => createArticleRouteHelper(null, $routes));

const siteContextOptionsList = computed(() => (
  props.site_context_options.length
    ? props.site_context_options
    : [{ value: 'code_medical_center', label: 'Centrum Medyczne CODE' }]
));

const displayTitle = computed(() => form.article.data_pl.title || form.article.data_en.title || '');

const page = usePage();
const statusErrors = computed(() => {
  const raw = page.props.errors?.['article.status'] || form.errors?.['article.status'] || [];
  return Array.isArray(raw) ? raw : [raw];
});

const isPublished = computed({
  get: () => form.article.status === 'published',
  set: (value) => {
    form.article.status = value ? 'published' : 'draft';
  },
});

function submitOptions() {
  const hasFile = form.article.cover_image instanceof File;
  return {
    forceFormData: hasFile,
    onSuccess: () => {
      form.article.cover_image = null;
    },
  };
}

function submit() {
  if (props.is_new) {
    form.post(routes.value.index(), submitOptions());
  } else {
    form.put(routes.value.update(props.article.id), submitOptions());
  }
}

function destroy() {
  if (!confirm('Usunąć tę aktualność?')) return;
  router.delete(routes.value.destroy(props.article.id));
}
</script>
